<template>
  <div class="popup z-index-10-fg confirmation small-popup options-popup">
    <div class="popup-content">
      <div class="popup-header">
        <h2>{{ name }}</h2>
        <img src="../assets/close.svg" alt="" class="popup-close" @click="$emit('closePopup')" />
      </div>

      <div class="scrollable-div" ref="scrollableDiv">
        <table class="full-width-table options-table">
          <tbody>
            <tr class="table-header">
              <th>Option Name</th>
              <th v-for="column in otherColumns" :key="column.name">{{ column.title }}</th>
              <th class="add-action">
                <button class="add-square-btn-small" @click="addOption()"><img src="../assets/plus-symbol.svg" />Add</button>
              </th>
            </tr>
            <tr v-for="option in localOptions" :key="option.PICKLISTITEMID">
              <td class="table-input">
                <span v-if="lockedOption(option)">{{ option.NAME }}</span>
                <input v-else v-model="option.NAME" @input="option.hasChanged = true" />
              </td>

              <td v-for="column in otherColumns" :key="column.name" :class="{ 'table-select': column.type === 'dropdown', 'table-input': column.type !== 'dropdown' }">
                <select v-if="column.type === 'dropdown'" v-model="option[column.name]" @change="option.hasChanged = true">
                  <option v-for="dropOption in column.dropdownOptions" :key="dropOption.value" :value="dropOption.value">
                    {{ dropOption.name }}
                  </option>
                </select>
                <input v-else v-model="option[column.name]" @input="option.hasChanged = true" />
              </td>

              <td v-if="!lockedOption(option)" class="close" @click="removeOption(option.PICKLISTITEMID)">
                <img src="../assets/close.svg" alt="" />
              </td>
              <td v-else></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="popup-footer">
        <button @click="$emit('closePopup')" class="text-btn">cancel</button>
        <button :disabled="isSaving" :class="{ 'square-btn': true, saving: isSaving }" @click="saveOptions()">Save<img src="../assets/spinner.svg" /></button>
      </div>
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
</template>

<script>
import usePicklistItemsDelete from '@/composables/usePicklistItemsDelete';
import usePicklistItemsAdd from '@/composables/usePicklistItemsAdd';
import usePicklistItemsUpdate from '@/composables/usePicklistItemsUpdate';
import useAlerts from '@/composables/useAlerts';
import useDecryptSymbols from '@/composables/useDecryptSymbols';

const PRICING_LEVEL_PICKLIST_ID = '62dd3ab2-48ca-45a0-9f59-3b36dd768daa';
const LOCKED_PRICING_LEVEL_IDS = new Set([
  '9354b9f5-572a-4665-ae4d-c9ce0e78aa70',
  'c0174ae6-17c3-4ab8-a0ea-2dc8897815e5',
  '016967cb-17c9-4ed9-910d-acf3493dfaab',
]);

export default {
  name: 'OptionsPopup',
  emits: ['closePopup'],
  props: {
    name: String,
    options: Array,
    picklistid: String,
    otherColumns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localOptions: [],
      optionsToRemove: [],
      isSaving: false,
    };
  },
  setup() {
    const { deletePicklistItems } = usePicklistItemsDelete();
    const { addPicklistItems } = usePicklistItemsAdd();
    const { updatePicklistItems } = usePicklistItemsUpdate();
    const { addAlert } = useAlerts();
    const { encryptSymbols } = useDecryptSymbols();

    return {
      deletePicklistItems,
      addPicklistItems,
      updatePicklistItems,
      addAlert,
      encryptSymbols,
    };
  },
  methods: {
    removeOption(id) {
      const option = this.localOptions.find((option) => option.PICKLISTITEMID === id);
      this.localOptions = this.localOptions.filter((option) => option.PICKLISTITEMID !== id);
      if (option.isNew) return;
      this.optionsToRemove.push(id);
    },
    addOption() {
      const newOption = { NAME: '', PICKLISTITEMID: Math.random().toString(), isNew: true };

      // Initialize values for other columns
      this.otherColumns.forEach((column) => {
        if (column.type === 'dropdown' && column.dropdownOptions.length > 0) {
          newOption[column.name] = column.dropdownOptions[0];
        } else {
          newOption[column.name] = '';
        }
      });

      this.localOptions.push(newOption);
      this.$nextTick(() => {
        const scrollableDiv = this.$refs.scrollableDiv;
        scrollableDiv.scrollTop = scrollableDiv.scrollHeight;
      });
    },
    async saveOptions() {
      this.isSaving = true;

      // Prepare new options with additional columns
      const newOptions = this.localOptions
        .filter((option) => option.isNew)
        .map((option) => {
          const newOption = {
            PICKLISTID: this.picklistid,
            NAME: option.NAME,
          };

          // Add other columns
          this.otherColumns.forEach((column) => {
            newOption[column.name] = option[column.name];
          });

          return newOption;
        });

      // Prepare updated options with additional columns
      const updatedOptions = this.localOptions
        .filter((option) => option.hasChanged && !option.isNew)
        .map((option) => {
          const updatedOption = {
            PICKLISTITEMID: option.PICKLISTITEMID,
            PICKLISTID: option.PICKLISTID,
            NAME: option.NAME,
          };

          // Add other columns
          this.otherColumns.forEach((column) => {
            updatedOption[column.name] = option[column.name];
          });

          return updatedOption;
        });

      if (this.optionsToRemove.length) await this.deletePicklistItems(this.optionsToRemove);
      if (newOptions.length) await this.addPicklistItems(this.encryptSymbols(newOptions));
      if (updatedOptions.length) await this.updatePicklistItems(this.encryptSymbols(updatedOptions));

      this.isSaving = false;
      this.addAlert('Options saved successfully', 'low', 3);
      this.$emit('closePopup');
    },
    lockedOption(option) {
      if (option.NAME === 'Confirmed Order' && this.picklistid === '199bbd82-6acf-4a25-9495-75e4aeea3ae6') return true;
      if (option.NAME === 'Sent Quote' && this.picklistid === '286aee1c-0d56-428f-83d1-f31ead643f5e') return true;
      if (this.picklistid?.toLowerCase() === PRICING_LEVEL_PICKLIST_ID && LOCKED_PRICING_LEVEL_IDS.has(String(option.PICKLISTITEMID || '').toLowerCase())) return true;
      return false;
    },
  },
  mounted() {
    this.localOptions = [...this.options];
    this.optionsToRemove = [];
  },
};
</script>

<style lang="scss" scoped>
.popup.options-popup {
  width: 30rem;
  padding: 0;

  .popup-content {
    display: flex;
    flex-direction: column;
    max-height: 50vh;
    overflow: auto;

    .popup-header {
      padding: 2rem 2rem 0 2rem;
    }

    .scrollable-div {
      .full-width-table.options-table {
        min-width: unset;

        td:first-child,
        th:first-child {
          padding-left: 2rem;
        }
      }

      .add-action {
        width: 2.2rem;

        button {
          margin-left: -5rem;
        }
      }

      .close {
        img {
          cursor: pointer;
          width: 1.25rem;
        }
      }
    }

    .popup-footer {
      box-shadow: 0px -4px 10px white;
      padding: 1rem 2rem 2rem 2rem;
    }
  }
}
</style>
